import { GroupsIcon, Table, useDefaultSorting } from '@bp/ui-components';
import { Row } from '@tanstack/react-table';
import { BpLink } from '../BpLink/BpLink';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useMatrixNotifications } from 'hooks/matrix/useMatrixNotifications';

const GroupsPreviewTableFieldsDocument = graphql(`
  fragment GroupsPreviewTableFields on Query {
    ...PermissionCheckerFields
    profileGroups: groups(
      where: {
        type: group
        organization: { uuid_IN: $organizations }
        OR: [{ editors_SOME: { uuid: $profile } }, { viewers_SOME: { uuid: $profile } }]
      }
    ) {
      uuid
      __typename
      name
      organization {
        uuid
        __typename
      }
      events(where: { virtualLocations_SOME: { running: true } }) {
        uuid
        __typename
      }
    }
  }
`);

export type GroupsPreviewTableType = {
  uuid: string;
  organization: { uuid: string };
  name: string;
  meetingInProgress: boolean;
  count?: number;
  newMaterial: boolean;
};

type GroupsPreviewTableProps = {
  onNavigate: (uuid: string) => void;
  query: FragmentType<typeof GroupsPreviewTableFieldsDocument> | undefined;
};

export const GroupsPreviewTable: FC<GroupsPreviewTableProps> = ({ onNavigate, query }) => {
  const { t } = useTranslation();
  const { sorting, onSortingChange } = useDefaultSorting();
  const fragment = getFragmentData(GroupsPreviewTableFieldsDocument, query);
  const matrixNotifications = useMatrixNotifications(fragment?.profileGroups.map((g) => g.uuid) ?? []);
  const profileGroups = fragment?.profileGroups;

  const data =
    profileGroups?.map((group) => {
      const notification = matrixNotifications.get(group.uuid);
      return {
        uuid: group.uuid,
        organization: group.organization,
        name: group.name,
        meetingInProgress: group.events.length > 0,
        count: notification?.unreadCount ?? 0,
        newMaterial: notification?.hasNewMessages ?? false,
      };
    }) ?? [];

  const permissions = usePermissionChecker(fragment);

  return (
    <Table<GroupsPreviewTableType>
      hideHeader
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      customPadding='var(--spacing-6)'
      customRowHeight='var(--list-row-height)'
      customLastColSpacing='0'
      breakpoint={null}
      data={data}
      columns={[
        {
          id: 'name',
          accessorKey: 'name',
          cell: ({ row }: { row: Row<GroupsPreviewTableType> }) => (
            <BpLink
              value={row.original.name}
              onNavigate={() => onNavigate(row.original.uuid)}
              inProgress={row.original.meetingInProgress}
              inProgressHint={t('groups.activeMeetingHint')}
              counter={row.original.count}
              counterHint={t('groups.newSubmissionsHint')}
              hasNews={row.original.newMaterial}
              hasNewsHint={t('groups.newMessageHint')}
              isEditorOfCourse={permissions?.isGroupEditor(row.original) ?? false}
            />
          ),
          canExpand: true,
        },
      ]}
      emptyStateSettings={{
        icon: <GroupsIcon className='svg-icon primary-light' />,
        size: 'small',
        title: '',
        subtitle: t('groups.noGroups'),
        padding: 'xl',
      }}
    />
  );
};
